import { Box, Chip, Paper, Stack, Typography } from '@mui/material';

import { Icon } from '@karehero/llama/components/atoms/Icon/Icon';

// B3 — not a screen. One way of thinking about where the data lives, drawn so
// the team has something concrete to disagree with. Three layers: global data
// entered once and reused, claim-specific detail, and raw unstructured capture
// that only becomes a claim answer when the user confirms it.

const Tier = ({
  label,
  caption,
  accent,
  children,
}: {
  label: string;
  caption: string;
  accent: string;
  children: React.ReactNode;
}) => (
  <Paper variant='outlined'>
    <Stack sx={{ p: 2 }} spacing={1.5}>
      <Stack sx={{ alignItems: 'center' }} direction='row' spacing={1.5}>
        <Box
          sx={{ width: 4, height: 16, borderRadius: '2px', bgcolor: accent }}
        />
        <Typography variant='overline'>{label}</Typography>
      </Stack>
      <Typography variant='caption' color='text.secondary'>
        {caption}
      </Typography>
      {children}
    </Stack>
  </Paper>
);

const Arrow = ({ label, up }: { label: string; up?: boolean }) => (
  <Stack sx={{ alignItems: 'center', pl: 1.5 }} direction='row' spacing={1.5}>
    <Box sx={{ color: 'text.secondary', display: 'flex' }}>
      <Icon
        icon={(up ? 'chevrons-up' : 'chevrons-down') as never}
        fontSize={14}
      />
    </Box>
    <Typography variant='caption' color='text.secondary'>
      {label}
    </Typography>
  </Stack>
);

const B3DataMap = () => (
  <Stack spacing={1.5}>
    <Tier
      label='Global'
      caption='Entered once, reused by every claim. Changing it changes it everywhere.'
      accent='custom.marineGreen.500'
    >
      <Stack sx={{ flexWrap: 'wrap' }} direction='row' spacing={1} useFlexGap>
        {[
          'Carer details',
          'Margaret’s details',
          'GP',
          'NI number',
          'Address',
        ].map((f) => (
          <Chip key={f} size='small' label={f} />
        ))}
      </Stack>
    </Tier>

    <Arrow label='flows into every claim' />

    <Tier
      label='Claim-specific'
      caption='The detail only this claim needs. Lives with the claim, not the person.'
      accent='custom.coral.600'
    >
      <Stack sx={{ alignItems: 'stretch' }} direction='row' spacing={1.5}>
        <Paper variant='outlined' sx={{ flex: 1 }}>
          <Stack sx={{ p: 1.5 }} spacing={1}>
            <Typography variant='subtitle2'>Attendance Allowance</Typography>
            <Stack
              sx={{ flexWrap: 'wrap' }}
              direction='row'
              spacing={0.5}
              useFlexGap
            >
              {['Washing', 'Night needs', 'Supervision'].map((f) => (
                <Chip key={f} size='small' variant='outlined' label={f} />
              ))}
            </Stack>
          </Stack>
        </Paper>
        <Paper variant='outlined' sx={{ flex: 1 }}>
          <Stack sx={{ p: 1.5 }} spacing={1}>
            <Typography variant='subtitle2'>Council Tax</Typography>
            <Stack
              sx={{ flexWrap: 'wrap' }}
              direction='row'
              spacing={0.5}
              useFlexGap
            >
              {['Savings', 'Who else lives there'].map((f) => (
                <Chip key={f} size='small' variant='outlined' label={f} />
              ))}
            </Stack>
          </Stack>
        </Paper>
      </Stack>
    </Tier>

    <Arrow
      label='promoted into a claim answer only when the user confirms it'
      up
    />

    <Tier
      label='Unstructured'
      caption='Raw capture. Kept even when it isn’t usable yet, and never silently turned into an answer.'
      accent='custom.cherry.500'
    >
      <Stack sx={{ flexWrap: 'wrap' }} direction='row' spacing={1} useFlexGap>
        {[
          'Voice notes',
          'Diary entries',
          'Brain-dumps',
          'Photos of letters',
        ].map((f) => (
          <Chip key={f} size='small' label={f} />
        ))}
      </Stack>
    </Tier>

    <Box
      sx={{
        mt: 1,
        p: 2,
        borderLeft: 2,
        borderColor: 'custom.warmOrange.500',
        bgcolor: 'custom.warmOrange.50',
        borderRadius: '0 12px 12px 0',
      }}
    >
      <Stack spacing={0.5}>
        <Typography variant='overline' color='text.secondary'>
          THE QUESTION THIS MAP IS MEANT TO FORCE
        </Typography>
        <Typography variant='body2'>
          What do we deliberately <strong>not</strong> store? Every layer here
          is additive, and a care record that keeps everything forever is a data
          swamp with a subject-access request attached.
        </Typography>
      </Stack>
    </Box>
  </Stack>
);

B3DataMap.displayName = 'B3DataMap';
export default B3DataMap;
